import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, apiError, fmtHour } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
// JS weekday: Python weekday() Mon=0..Sun=6 (matches our index)

export default function StudioHours() {
  const { data } = useQuery({ queryKey: ["studio-hours-admin"], queryFn: () => api.get("/studio-hours").then((r) => r.data) });
  const [hours, setHours] = useState({});

  useEffect(() => {
    if (data?.hours) setHours(data.hours);
  }, [data]);

  const update = (d, key, val) => setHours((h) => ({ ...h, [d]: { ...h[d], [key]: val } }));

  const save = async () => {
    try {
      await api.put("/studio-hours", { hours });
      toast.success("Studio hours saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-studio-hours">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl text-white mb-1">Studio Hours</h1>
          <p className="text-white/50 text-sm">Set open/close hours per weekday (1-hour slots). Default is 24/7.</p>
        </div>
        <button onClick={save} data-testid="save-hours-btn" className="btn-gold rounded-full px-6 py-2.5 text-sm font-mono-label uppercase flex items-center gap-2"><Save size={15} /> Save</button>
      </div>

      <div className="space-y-2 max-w-2xl">
        {DAYS.map((day, idx) => {
          const cfg = hours[String(idx)] || { open: true, start: 0, end: 24 };
          return (
            <div key={day} className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-[#111] border border-white/10 rounded-xl px-4 sm:px-5 py-3" data-testid={`hours-row-${idx}`}>
              <span className="w-28 text-white text-sm">{day}</span>
              <Switch checked={cfg.open !== false} onCheckedChange={(v) => update(String(idx), "open", v)} data-testid={`open-switch-${idx}`} />
              {cfg.open !== false ? (
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <select value={cfg.start} onChange={(e) => update(String(idx), "start", Number(e.target.value))} className="bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 [color-scheme:dark]" data-testid={`start-${idx}`}>
                    {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{fmtHour(h)}</option>)}
                  </select>
                  <span>to</span>
                  <select value={cfg.end} onChange={(e) => update(String(idx), "end", Number(e.target.value))} className="bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 [color-scheme:dark]" data-testid={`end-${idx}`}>
                    {Array.from({ length: 25 }, (_, h) => <option key={h} value={h}>{fmtHour(h)}</option>)}
                  </select>
                </div>
              ) : <span className="text-white/40 text-sm">Closed</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
